import { PageHeading } from '@src/components/PageHeading/PageHeading';
import { hasKeyboardViewport } from './keyboardViewport';
import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@src/components/Button/Button';
import { Panel } from '@src/components/Panel/Panel';
import { Cluster } from '@src/components/Cluster/Cluster';
import { Container } from '@src/components/Container/Container';
import { CreationHeader } from '@src/components/CreationHeader/CreationHeader';
import { Footer } from '@src/components/Footer/Footer';
import { Page } from '@src/components/Page/Page';
import { Stack } from '@src/components/Stack/Stack';
import './CreationShell.css';
import './CreationShellMobile.css';

export interface CreationShellProps {
  /** Short name in the header — “Choose game name”. */
  heading: ReactNode;
  /** Optional progress chrome; navigation stays inside the panel when hidden. */
  showHeader?: boolean;
  /** Page H1 — “What do you want to name your game?” */
  title: ReactNode;
  subtitle?: ReactNode;
  /** 1-based. */
  step: number;
  steps: number;
  onBack?: () => void;
  backLabel?: string;
  /** Hide the in-card Back. The header chevron still shows if `onBack` is set. */
  hideBack?: boolean;
  /** Primary action — Continue, Create. */
  action?: ReactNode;
  /** The step’s form or choices. */
  children: ReactNode;
  /** Optional content below the form, outside its panel. */
  afterForm?: ReactNode;
  error?: ReactNode;
  /** Nested specimen — drop the viewport height, sticky header and footer. */
  contained?: boolean;
  /** Inverse site footer. Off only for a nested specimen. */
  footer?: boolean;
  testId?: string;
  /** Opt-in viewport layout for mobile game creation. */
  mobileFocused?: boolean;
  mobileBare?: boolean;
  decorated?: boolean;
  mobileProgress?: boolean;
  stepKey?: string;
}

/**
 * Shared create-flow chrome: progress header, a themed landscape, the
 * step title, and a festive card for the fields. Box, card and quick-draw
 * wizards pass data in — they do not rebuild this layout.
 */
export function CreationShell({
  heading,
  showHeader = true,
  title,
  subtitle,
  step,
  steps,
  onBack,
  backLabel,
  hideBack = false,
  action,
  children,
  afterForm,
  error,
  contained = false,
  footer = true,
  testId,
  mobileFocused = false,
  mobileBare = false,
  decorated = true,
  mobileProgress = true,
  stepKey,
}: CreationShellProps) {
  const { t } = useTranslation();
  const shellRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!mobileFocused) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const viewport = window.visualViewport;
    const shell = shellRef.current;
    // Measure the CSS viewport independently of the keyboard-sized shell.
    const viewportMeasure = document.createElement('div');
    viewportMeasure.style.cssText =
      'position:fixed;top:0;width:0;height:var(--creation-full-height, 100dvh);visibility:hidden;pointer-events:none';
    viewportMeasure.setAttribute('aria-hidden', 'true');
    shell?.appendChild(viewportMeasure);
    let frame = 0;
    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    const update = () => {
      const editing =
        shell?.contains(document.activeElement) &&
        document.activeElement?.matches(
          'input:not([type="checkbox"]):not([type="radio"]), textarea, [contenteditable="true"]',
        );
      const keyboardOpen =
        viewport &&
        hasKeyboardViewport(
          Boolean(editing),
          viewportMeasure.getBoundingClientRect().height,
          viewport.height,
          viewport.scale,
        );
      if (keyboardOpen && viewport) {
        shell?.style.setProperty('--creation-viewport-height', `${viewport.height}px`);
        shell?.style.setProperty('--creation-viewport-top', `${viewport.offsetTop}px`);
      } else {
        shell?.style.removeProperty('--creation-viewport-height');
        shell?.style.removeProperty('--creation-viewport-top');
      }
    };
    const scheduleUpdate = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
      clearTimeout(settleTimer);
      settleTimer = setTimeout(update, 350);
    };
    update();
    viewport?.addEventListener('resize', scheduleUpdate);
    viewport?.addEventListener('scroll', scheduleUpdate);
    window.addEventListener('resize', scheduleUpdate);
    document.addEventListener('focusin', scheduleUpdate);
    document.addEventListener('focusout', scheduleUpdate);
    return () => {
      document.body.style.overflow = previous;
      cancelAnimationFrame(frame);
      clearTimeout(settleTimer);
      viewportMeasure.remove();
      viewport?.removeEventListener('resize', scheduleUpdate);
      viewport?.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      document.removeEventListener('focusin', scheduleUpdate);
      document.removeEventListener('focusout', scheduleUpdate);
      shell?.style.removeProperty('--creation-viewport-height');
      shell?.style.removeProperty('--creation-viewport-top');
    };
  }, [mobileFocused, step, stepKey]);
  useEffect(() => {
    if (mobileFocused) shellRef.current?.querySelector('.creation-shell__main')?.scrollTo(0, 0);
  }, [step, stepKey, mobileFocused]);
  useEffect(() => {
    if (!mobileFocused) return;
    const shell = shellRef.current;
    if (!shell) return;
    let previousTouch: { x: number; y: number } | null = null;
    const start = (event: TouchEvent) => {
      previousTouch =
        event.touches.length === 1
          ? { x: event.touches[0].clientX, y: event.touches[0].clientY }
          : null;
    };
    const move = (event: TouchEvent) => {
      if (!previousTouch || event.touches.length !== 1) return;
      const touch = event.touches[0];
      const dx = touch.clientX - previousTouch.x;
      const dy = touch.clientY - previousTouch.y;
      previousTouch = { x: touch.clientX, y: touch.clientY };
      if (!dy || Math.abs(dx) > Math.abs(dy)) return;
      // Short overflow areas can still rubber-band on iOS. Let nested fields
      // and the main area scroll only when they have room in this direction.
      let node = event.target instanceof Element ? event.target : null;
      while (node && shell.contains(node)) {
        const maxScroll = node.scrollHeight - node.clientHeight;
        if (
          maxScroll > 1 &&
          /auto|scroll/.test(getComputedStyle(node).overflowY) &&
          ((dy < 0 && node.scrollTop < maxScroll - 1) || (dy > 0 && node.scrollTop > 1))
        )
          return;
        if (node === shell) break;
        node = node.parentElement;
      }
      if (event.cancelable) event.preventDefault();
    };
    const end = () => {
      previousTouch = null;
    };
    shell.addEventListener('touchstart', start, { passive: true });
    shell.addEventListener('touchmove', move, { passive: false });
    shell.addEventListener('touchend', end);
    shell.addEventListener('touchcancel', end);
    return () => {
      shell.removeEventListener('touchstart', start);
      shell.removeEventListener('touchmove', move);
      shell.removeEventListener('touchend', end);
      shell.removeEventListener('touchcancel', end);
    };
  }, [mobileFocused]);
  const back = backLabel ?? t('creation.back');
  const showCardBack = Boolean(onBack) && !hideBack;
  const showFooter = showCardBack || Boolean(action);
  const shellClass = [
    'creation-shell',
    mobileFocused && 'creation-shell--mobile-focused',
    mobileFocused && mobileBare && 'creation-shell--bare',
    contained && 'creation-shell--contained',
    !showHeader && 'creation-shell--headerless',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Page flush={contained} className={mobileFocused ? 'page--creation-focused' : undefined}>
      <div
        ref={shellRef}
        data-step={stepKey}
        className={shellClass}
        onKeyDown={(event) => {
          // Multiline fields and nested forms retain their own Enter behavior.
          if (
            event.key !== 'Enter' ||
            event.defaultPrevented ||
            event.repeat ||
            event.nativeEvent.isComposing ||
            event.keyCode === 229 ||
            event.shiftKey ||
            event.ctrlKey ||
            event.altKey ||
            event.metaKey ||
            !(event.target instanceof HTMLInputElement) ||
            event.target.form ||
            !['text', 'email', 'tel', 'number', 'password', 'url', 'search'].includes(
              event.target.type,
            )
          )
            return;
          const submit = event.currentTarget.querySelector<HTMLButtonElement>(
            '.creation-shell__action button',
          );
          if (submit) {
            event.preventDefault();
            if (!submit.disabled) submit.click();
          }
        }}
      >
        {showHeader && (
          <CreationHeader
            title={heading}
            step={step}
            steps={steps}
            onBack={onBack}
            backLabel={back}
            sticky={!contained}
            testId={testId ? `${testId}-header` : undefined}
          />
        )}

        {mobileFocused && mobileProgress && (
          <div className="creation-shell__mobile-progress">
            <span>{heading}</span>
            <span>
              {step} / {steps}
            </span>
            <progress
              value={step}
              max={steps}
              aria-label={typeof heading === 'string' ? heading : undefined}
            />
          </div>
        )}
        <Container
          width="medium"
          gutter="default"
          pad="page"
          grow
          as="main"
          className="creation-shell__main"
          testId={testId}
        >
          <Stack gap={8} wide className="creation-shell__body">
            <Stack
              gap={3}
              align="center"
              as="header"
              wide
              className="creation-shell__intro landscape-sky-content"
            >
              <PageHeading title={title} subtitle={subtitle} />
            </Stack>
            <Panel
              elevated
              wide
              edges={!decorated || (mobileFocused && mobileBare) ? undefined : { pieces: 3 }}
              className="creation-shell__card"
            >
              <Stack gap={6} wide className="creation-shell__card-inner">
                {children}
                {error}
                {showFooter && !mobileFocused ? (
                  <Cluster
                    justify={showCardBack ? 'between' : 'end'}
                    align="center"
                    wide
                    stack="narrow"
                    className="creation-shell__card-foot"
                  >
                    {showCardBack ? (
                      <Button
                        variant="ghost"
                        className="creation-shell__card-back"
                        onClick={onBack}
                        testId={testId ? `${testId}-back` : undefined}
                      >
                        {back}
                      </Button>
                    ) : null}
                    {action ? (
                      <div key={stepKey ?? step} className="creation-shell__action">
                        {action}
                      </div>
                    ) : null}
                  </Cluster>
                ) : null}
              </Stack>
            </Panel>
          </Stack>
        </Container>
        {afterForm}
        {mobileFocused && (
          <div className="creation-shell__mobile-actions">
            {showCardBack && (
              <Button
                variant="ghost"
                onClick={onBack}
                testId={testId ? `${testId}-back` : undefined}
              >
                {back}
              </Button>
            )}
            <div key={stepKey ?? step} className="creation-shell__action">
              {action}
            </div>
          </div>
        )}
      </div>
      {footer && !contained && !mobileFocused ? <Footer width="content" /> : null}
    </Page>
  );
}
